/**
 * Alfie API Client for Prometheus
 *
 * Provides React Query hooks and API functions for integrating with the Hermes Alfie backend.
 *
 * Endpoints:
 * - POST /alfie-chat - Send chat message (user context is assembled server-side)
 * - POST /alfie-files - Upload files (multipart/form-data)
 * - Database queries for threads, messages, projects, personas
 */

import { EDGE_FUNCTION_HEADERS } from '@/lib/edge-functions';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { api } from './client';

// ============================================================================
// UUID helpers
// ============================================================================

/** Canonical RFC-4122 UUID matcher. Used to tell a real thread id apart from
 *  the transient `temp-pending` sentinel and any other non-uuid selection. */
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Type-guard: true only for a well-formed UUID string. Narrows `string | null`
 *  to `string` so callers can pass the value straight through as a thread id. */
export function isUuid(s?: string | null): s is string {
  return typeof s === 'string' && UUID_RE.test(s);
}

// ============================================================================
// Types (aligned with Hermes contract)
// ============================================================================

/**
 * How a plan card's button reaches the purchase flow. Comes from the product's
 * `purchase_action` in the database catalog (Hermes `_shared/alfie/packaging.ts`, context v2,
 * 12 Oct 2026); the set of values is fixed by a CHECK constraint.
 */
export type PlanAction =
  | 'buy'
  | 'selector'
  | 'application'
  | 'inquire'
  | 'contact'
  | 'invitation'
  | 'none';

/** Brochure copy for one product, from its `catalog_copy` in the database catalog. */
export interface PlanBrochureCopy {
  name: string;
  badge: string | null;
  pill: string | null;
  summary: string | null;
  bestFor: string | null;
  hero: { headline: string; sub: string | null };
  bullets: string[];
  seatsLine: string | null;
  ctaLabel: string | null;
  flagship: boolean;
  dark: boolean;
  includesAlfieFree: boolean;
}

/**
 * One plan card under a package answer. Hermes builds these from the codes Alfie offered,
 * with the price label taken from the mirrored catalog, and sends them on the `done` event
 * (`StreamDoneData.planCards`), on `ChatResponse.planCards`, and in
 * `alfie_messages.metadata.planCards`. The member's email is added by `PlanCards` at render.
 */
export interface PlanCardData {
  code: string;
  /** Product family. A string so a family the catalog adds later still renders. */
  axis: 'alfie' | 'kc' | 'addon' | (string & {});
  priceLabel: string;
  action: PlanAction;
  linkable: boolean;
  copy: PlanBrochureCopy;
}

/**
 * A citation Alfie attaches to an answer, already resolved by Hermes against
 * the caller's entitlements. Arrives on the streaming `done` event
 * (`StreamDoneData.citations`), on the non-streaming `ChatResponse`, and is
 * persisted in `alfie_messages.metadata.citations`.
 *
 * `state` drives rendering (see AlfieContent):
 *  - `available`   — resolvable; `url` is an internal path (or null for a chip,
 *                    e.g. an expert with no dedicated page).
 *  - `locked`      — exists but gated behind a higher plan/tier.
 *  - `unavailable` — cannot be resolved; render as plain text, never a link.
 *
 * `legacy` marks a citation reconstructed from an old `{ name, entity_type }`
 * marker with no resolved target — AlfieContent falls back to the library
 * name-search path for these.
 */
export interface ResolvedCitation {
  entityId: string;
  entityType: string;
  name: string;
  label: string;
  url: string | null;
  state: 'available' | 'locked' | 'unavailable';
  legacy?: boolean;
}

export interface ChatRequest {
  message: string;
  threadId?: string;
  projectId?: string;
  personaId?: string;
  style?: 'normal' | 'learning' | 'concise' | 'explanatory' | 'formal';
  model?: 'claude' | 'gpt' | 'gemini' | 'grok';
  category?: string;
  webSearch?: boolean;
  fileIds?: string[];
  fromChat?: boolean;
  chatConversationId?: string;
  /** Where the member typed it. Absent means the /alfie page. Stored on the message by Hermes. */
  surface?: ChatSurface;
  /**
   * What `activities.entity_name` records for this send, INSTEAD of the first 80 characters of
   * the message.
   *
   * Every Alfie message's opening 80 characters are logged there (see `trackAlfieMessageSent`).
   * That is fine for prose a member typed, and wrong for a message carrying context we assembled:
   * the Xero mapping chat prepends an account's code and name so Alfie can answer about it, and
   * Tim's rule is that no account name, code or balance reaches that table. A caller that builds
   * such a preamble sets a redacted preview here. Nothing else in the app passes it, so no other
   * message's logging changes.
   */
  activityPreview?: string;
  /**
   * 'finance_info': the finance "Ask Alfie" panel. Hermes then sends Alfie this builder's figures
   * and what each one means, and Alfie answers about the books only — no coaching move, Prime
   * Score, pillars or course suggestions (Tim, 12 Oct 2026). Sent on every message from that panel.
   */
  mode?: 'finance_info';
  /**
   * The finance brief for this question (the normal Alfie on the Xero screens). Hermes saves it
   * beside the question, not in it, so it never shows as the member's own words, and sends Alfie
   * `brief\n\nquestion` — what the ✦ buttons send. Only honoured with `mode: 'finance_info'`.
   */
  contextBrief?: string;
}

export type ChatSurface = 'widget' | 'page';

export interface ChatResponse {
  threadId: string;
  threadCreated?: boolean;
  response: string;
  model: string;
  responseTime: number;
  tokensUsed?: {
    input: number;
    output: number;
    total: number;
  };
  suggestedTasks?: Array<{
    title: string;
    priority: 'low' | 'medium' | 'high';
    estimatedDuration?: number;
  }>;
  insights?: {
    userFacts?: Array<{ fact: string; confidence: number }>;
    detectedIntents?: Array<{ intent: string; confidence: number }>;
  };
  conversationSummary?: string;
  /** Resolved citations for this answer (Hermes contract). */
  citations?: ResolvedCitation[];
  /** Plan cards under a package answer. Absent on every other answer. */
  planCards?: PlanCardData[];
}

export interface Thread {
  id: string;
  title: string;
  context_type: string;
  project_id?: string;
  persona_id?: string;
  is_archived: boolean;
  last_message_at: string;
  created_at: string;
  updated_at?: string;
  from_chat?: boolean;
  chat_conversation_id?: string;
  /** Set when the user pins a thread to the top of the widget history list.
   *  Added by hermes migration 592 — see PINNED_COLUMN_FALLBACK below. */
  pinned_at?: string | null;
}

export interface Message {
  id: string;
  thread_id: string;
  role: 'user' | 'assistant';
  content: string;
  metadata?: {
    model?: string;
    tokensUsed?: { input: number; output: number; total: number };
    suggestedTasks?: Array<{
      title: string;
      priority: 'low' | 'medium' | 'high';
      estimatedDuration?: number;
    }>;
    insights?: {
      userFacts?: Array<{ fact: string; confidence: number }>;
      detectedIntents?: Array<{ intent: string; confidence: number }>;
    };
    files?: FileAttachmentMeta[];
    /** Resolved citations persisted with the assistant message. */
    citations?: ResolvedCitation[];
    /** Plan cards persisted with a package answer. */
    planCards?: PlanCardData[];
  };
  file_ids?: string[];
  created_at: string;
}

export interface FileUpload {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  storageUrl: string;
  downloadUrl?: string;
  lines?: number;
  uploadedAt: string;
}

/** Lightweight file metadata attached to chat messages for display */
export interface FileAttachmentMeta {
  id: string;
  name: string;
  size: number;
  mimeType: string;
}

/** Row from alfie_files table */
export interface AlfieFile {
  id: string;
  name: string;
  size: number;
  mime_type: string;
  storage_path: string;
  lines: number | null;
  project_id: string | null;
  persona_id: string | null;
  uploaded_at: string;
}

export interface AlfieProject {
  id: string;
  name: string;
  description?: string;
  status?: 'active' | 'completed' | 'on_hold' | 'planned';
  progress?: number;
  memory?: string;
  instructions?: string;
  taskCount?: number;
  completedTaskCount?: number;
  conversationCount?: number;
  fileCount?: number;
  goalId?: string;
  goalName?: string;
  startDate?: string;
  endDate?: string;
  createdAt?: string;
  updatedAt?: string;
  source?: 'alfie_generated' | 'manual';
  isExample?: boolean;
}

export interface CreateProjectInput {
  name: string;
  description?: string;
  status?: 'active' | 'planned';
  goalId?: string;
  startDate?: string;
  endDate?: string;
  instructions?: string;
}

export interface UpdateProjectInput {
  name?: string;
  description?: string;
  status?: 'active' | 'completed' | 'on_hold' | 'planned';
  instructions?: string;
  memory?: string;
  goalId?: string;
  startDate?: string;
  endDate?: string;
  progress?: number;
}

export interface ProjectDetail {
  project: AlfieProject;
  tasks: Array<{
    id: string;
    title: string;
    status: string;
    priority: string;
    due_date?: string;
    ai_generated?: boolean;
    created_at: string;
  }>;
  files: Array<{
    id: string;
    name: string;
    size: number;
    mime_type: string;
    lines?: number;
    uploaded_at: string;
  }>;
  conversations: Array<{
    id: string;
    title: string;
    last_message_at: string;
  }>;
}

export interface AlfiePersona {
  id: string;
  name: string;
  description?: string;
  role?: string;
  expertise?: string[];
  instructions?: string;
  tone?: string;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// File Upload Validation
// ============================================================================

/** Per-message attachment limits. Exported so composers enforce THESE numbers
 *  rather than keeping their own copy that drifts out of step with the check
 *  `uploadFiles` actually runs. */
export const MAX_FILES = 5;
export const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/json',
  'text/html',
  'text/css',
  'text/javascript',
  'application/javascript',
];

// ============================================================================
// API Functions
// ============================================================================

async function callAlfieAPI<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: unknown
): Promise<T> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session?.access_token) {
    throw new Error('Not authenticated');
  }

  const hermesUrl = import.meta.env.VITE_HERMES_URL || import.meta.env.VITE_SUPABASE_URL;
  const hermesKey = import.meta.env.VITE_HERMES_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

  const url = `${hermesUrl}/functions/v1/${endpoint}`;

  const response = await fetch(url, {
    method,
    headers: {
      ...EDGE_FUNCTION_HEADERS,
      'Authorization': `Bearer ${session.session.access_token}`,
      'apikey': hermesKey,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    // structuredErrorResponse nests { error: { code, message } } — surface a
    // string (not "[object Object]") and carry the machine-readable code,
    // same contract as sendChatMessageStreaming.
    const nested = typeof error.error === 'object' && error.error !== null ? error.error : undefined;
    const thrown = new Error(
      nested?.message || nested?.code || error.message || error.error || 'Request failed',
    ) as Error & { code?: string };
    thrown.code = nested?.code;
    throw thrown;
  }

  return response.json();
}

export async function sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
  const response = await callAlfieAPI<ChatResponse>('alfie-chat', 'POST', request);
  trackAlfieMessageSent(response.threadId, request);
  return response;
}

function trackAlfieMessageSent(threadId: string, request: ChatRequest): void {
  api
    .logActivity({
      type: 'alfie',
      action: 'sent',
      entity_type: 'alfie_message',
      entity_id: threadId,
      entity_name: request.activityPreview ?? ((request.message || '').slice(0, 80) || '[empty]'),
      description: `Sent Alfie message in thread ${threadId}`,
      metadata: {
        thread_id: threadId,
        project_id: request.projectId,
        persona_id: request.personaId,
        category: request.category,
        style: request.style,
        model: request.model,
        web_search: request.webSearch === true,
        has_files: (request.fileIds?.length ?? 0) > 0,
        from_chat: request.fromChat === true,
        surface: request.surface ?? 'page',
      },
    })
    .catch(() => {
      // Non-critical: tracking failure must not affect chat UX.
    });
}

// ============================================================================
// Streaming types
// ============================================================================

export interface StreamMetadata {
  intent: string;
  model_tier: string;
}

export interface StreamDoneData {
  response: string;
  model: string;
  model_tier: string;
  /** Resolved citations (Hermes contract). Older messages may carry none. */
  citations: ResolvedCitation[];
  suggested_tasks: Array<{ title: string; priority: string }> | null;
  insights: unknown | null;
  conversation_summary: string | null;
  /** Plan cards under a package answer. Absent on every other answer. */
  planCards?: PlanCardData[];
}

export interface StreamSavedData {
  messageId: string;
  threadId: string;
  /** Present when Hermes created (rather than reused) the thread for this send. */
  threadCreated?: boolean;
  /** Why a new thread was created — lets the FE distinguish an expected new
   *  conversation from a server-side rejection of a bad/expired thread id. */
  threadCreationReason?: 'new_conversation' | 'invalid_thread_id';
}

/** Early SSE event (first frame) announcing the thread this send is bound to.
 *  Emitted by newer Hermes builds before any chunk; absent on older servers. */
export interface StreamThreadData {
  threadId: string;
  threadCreated?: boolean;
  threadCreationReason?: string;
}

// ============================================================================
// Streaming chat function
// ============================================================================

/**
 * Send a chat message and receive the response as a token-by-token stream.
 *
 * Calls Hermes with `Accept: text/event-stream` and parses SSE events.
 * The non-streaming fallback is `sendChatMessage`.
 *
 * @param request - Chat request params
 * @param onChunk - Called for each LLM token group
 * @param onMetadata - Called once before generation with intent + model info
 * @param onDone - Called when generation completes with full response data
 * @param onSaved - Called when DB write confirms message ID
 * @param onError - Called if an error event is received
 * @param signal - Optional AbortSignal for cancellation
 * @param onThread - Called on the early `thread` event (newer servers only) with
 *   the thread this send is bound to. Optional and last so existing positional
 *   callers (which end at `signal`) keep working unchanged.
 */
export interface StreamChunkContext {
  threadId: string;
  messageId: string;
}

export async function sendChatMessageStreaming(
  request: ChatRequest,
  onChunk: (text: string, context: StreamChunkContext) => void,
  onMetadata?: (data: StreamMetadata) => void,
  onDone?: (data: StreamDoneData) => void,
  onSaved?: (data: StreamSavedData) => void,
  onError?: (error: string, code?: string, retryable?: boolean) => void,
  signal?: AbortSignal,
  onThread?: (data: StreamThreadData) => void,
): Promise<void> {
  // `getSession()` is what keeps this send authenticated: auth-js refreshes
  // inside EXPIRY_MARGIN_MS (90s), so the token below always has ≥ ~90s left.
  // Hermes `alfie-chat` refuses to stream under 60s — deliberately below that
  // margin, so a token this call considers healthy can never be refused there.
  // If you ever shorten the refresh margin, raise the Hermes number to match;
  // the two are coupled with nothing but these comments enforcing it.
  const { data: session } = await supabase.auth.getSession();
  if (!session.session?.access_token) {
    throw new Error('Not authenticated');
  }

  const hermesUrl = import.meta.env.VITE_HERMES_URL || import.meta.env.VITE_SUPABASE_URL;
  const hermesKey = import.meta.env.VITE_HERMES_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

  const response = await fetch(`${hermesUrl}/functions/v1/alfie-chat`, {
    method: 'POST',
    headers: {
      ...EDGE_FUNCTION_HEADERS,
      'Authorization': `Bearer ${session.session.access_token}`,
      'apikey': hermesKey,
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    // structuredErrorResponse nests { error: { code, message } }; flat shapes
    // put a string in `message`/`error`. Surface a string either way, and carry
    // the machine-readable code so callers can special-case (e.g. the 403
    // entitlement refusal renders as an upgrade prompt, not a generic error).
    const nested = typeof error.error === 'object' && error.error !== null ? error.error : undefined;
    const thrown = new Error(
      nested?.message || nested?.code || error.message || error.error || `HTTP ${response.status}`,
    ) as Error & { code?: string };
    thrown.code = nested?.code;
    throw thrown;
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by double newlines
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';

      for (const part of parts) {
        // Skip SSE comments (e.g., `: thread-id=...`)
        if (part.startsWith(':')) continue;

        const match = part.match(/^data: (.+)$/m);
        if (!match) continue;

        let event: Record<string, unknown>;
        try {
          event = JSON.parse(match[1]);
        } catch {
          // Skip malformed events
          continue;
        }

        switch (event.type) {
          case 'thread':
            // Early bind event (first frame on newer servers). Tolerate a
            // malformed/missing threadId by simply not firing the callback.
            if (typeof event.threadId === 'string') {
              onThread?.({
                threadId: event.threadId,
                threadCreated: event.threadCreated as boolean | undefined,
                threadCreationReason: event.threadCreationReason as string | undefined,
              });
            }
            break;
          case 'chunk':
            if (typeof event.content === 'string') {
              onChunk(event.content, {
                threadId: event.threadId as string,
                messageId: event.messageId as string,
              });
            }
            break;
          case 'metadata':
            onMetadata?.(event.data as StreamMetadata);
            break;
          case 'done':
            onDone?.(event.data as StreamDoneData);
            break;
          case 'saved': {
            const savedData = event as unknown as StreamSavedData;
            onSaved?.(savedData);
            if (savedData?.threadId) trackAlfieMessageSent(savedData.threadId, request);
            break;
          }
          case 'error':
            onError?.(
              String(event.error || 'Unknown error'),
              event.code as string | undefined,
              event.retryable as boolean | undefined,
            );
            break;
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/** Page size used when a caller does not ask for one. */
export const THREADS_PAGE_SIZE = 50;

/**
 * `alfie_threads.pinned_at` ships in hermes migration 592, which lands in a
 * separate PR. Against a database that predates it, PostgREST answers the
 * ordered select with 42703 (undefined_column). Rather than break the whole
 * history list, the first such failure latches this flag and every later read
 * orders on `last_message_at` alone — pins simply do not float until the
 * migration is applied.
 *
 * TODO(migration 592): delete this flag and the retry once 592 is deployed to
 * every environment this build talks to.
 */
let pinnedColumnMissing = false;

function isUndefinedColumn(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return error.code === '42703' || /pinned_at/.test(error.message ?? '');
}

/**
 * Escapes a user-typed needle for SQL `LIKE`. `%` and `_` are wildcards there,
 * so an unescaped `_` would silently match any character — and the backslash
 * itself has to go first or it would escape the escapes we add after it.
 */
function escapeLikePattern(value: string): string {
  return value.replace(/[\\%_]/g, (char) => `\\${char}`);
}

export async function getThreads(
  {
    limit = THREADS_PAGE_SIZE,
    offset = 0,
    search,
  }: {
    /** `null` reads every row — the /alfie sidebar's flat list. */
    limit?: number | null;
    offset?: number;
    /** Case-insensitive title match, applied server-side. */
    search?: string;
  } = {},
): Promise<Thread[]> {
  const filtered = () => {
    const query = supabase
      .from('alfie_threads')
      .select('*')
      .eq('is_archived', false);

    return search ? query.ilike('title', `%${escapeLikePattern(search)}%`) : query;
  };

  const run = (withPinned: boolean) => {
    // Pinned first (newest pin wins), then the usual recency order. Postgres
    // sorts NULLs first on DESC, which would float every UNpinned thread to
    // the top — `nullsFirst: false` is load-bearing, not decoration.
    const ordered = withPinned
      ? filtered()
          .order('pinned_at', { ascending: false, nullsFirst: false })
          .order('last_message_at', { ascending: false })
      : filtered().order('last_message_at', { ascending: false });

    return limit === null ? ordered : ordered.range(offset, offset + limit - 1);
  };

  const { data, error } = await run(!pinnedColumnMissing);
  if (error && !pinnedColumnMissing && isUndefinedColumn(error)) {
    pinnedColumnMissing = true;
    const retry = await run(false);
    if (retry.error) throw retry.error;
    return retry.data || [];
  }
  if (error) throw error;
  return data || [];
}

/** Rename a thread. RLS restricts the update to the thread's owner. */
export async function renameThread(threadId: string, title: string): Promise<void> {
  const { error } = await supabase
    .from('alfie_threads')
    .update({ title })
    .eq('id', threadId);
  if (error) throw error;
}

/** Soft-delete: archived threads drop out of every list read (`is_archived` filter). */
export async function archiveThread(threadId: string): Promise<void> {
  const { error } = await supabase
    .from('alfie_threads')
    .update({ is_archived: true })
    .eq('id', threadId);
  if (error) throw error;
}

export async function setThreadPinned(threadId: string, pinned: boolean): Promise<void> {
  const { error } = await supabase
    .from('alfie_threads')
    .update({ pinned_at: pinned ? new Date().toISOString() : null })
    .eq('id', threadId);
  if (error) throw error;
}

export async function getCommunityThreads(): Promise<Thread[]> {
  const { data, error } = await supabase
    .from('alfie_threads')
    .select('*')
    .eq('from_chat', true)
    .eq('is_archived', false)
    .order('last_message_at', { ascending: false })
    .limit(20);

  if (error) throw error;
  return data ?? [];
}

export async function findChatThread(conversationId: string): Promise<string | null> {
  const { data } = await supabase
    .from('alfie_threads')
    .select('id')
    .eq('from_chat', true)
    .eq('chat_conversation_id', conversationId)
    .eq('is_archived', false)
    .order('last_message_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return data?.id ?? null;
}

export async function getThreadMessages(threadId: string): Promise<Message[]> {
  const { data, error } = await supabase
    .from('alfie_messages')
    .select('*')
    .eq('thread_id', threadId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function getProjects(): Promise<AlfieProject[]> {
  const result = await callAlfieAPI<{ projects: AlfieProject[] }>('alfie-projects');
  return result.projects;
}

export async function getProject(projectId: string): Promise<ProjectDetail> {
  return callAlfieAPI<ProjectDetail>(`alfie-projects/${projectId}`);
}

export async function createProject(input: CreateProjectInput): Promise<AlfieProject> {
  const result = await callAlfieAPI<{ project: AlfieProject }>('alfie-projects', 'POST', input);
  return result.project;
}

export async function updateProject(projectId: string, input: UpdateProjectInput): Promise<AlfieProject> {
  const result = await callAlfieAPI<{ project: AlfieProject }>(`alfie-projects/${projectId}`, 'PUT', input);
  return result.project;
}

export async function regenerateProjectMemory(projectId: string): Promise<{ memory: string; updatedAt: string }> {
  return callAlfieAPI<{ memory: string; updatedAt: string }>(`alfie-projects/${projectId}/regenerate-memory`, 'POST');
}

export async function getPersonas(): Promise<AlfiePersona[]> {
  const result = await callAlfieAPI<{ personas: AlfiePersona[] }>('alfie-personas');
  return result.personas;
}

export interface PersonaKnowledgeFile {
  id: string;
  name: string;
  size: number;
  mime_type: string;
  uploaded_at: string;
}

export interface PersonaDetailResponse {
  persona: AlfiePersona & {
    fileCount?: number;
    conversationCount?: number;
    type?: 'builtin' | 'custom';
  };
  files: PersonaKnowledgeFile[];
  recentConversations: Array<{ id: string; title: string; last_message_at: string }>;
}

export async function getPersona(personaId: string): Promise<PersonaDetailResponse> {
  return callAlfieAPI<PersonaDetailResponse>(`alfie-personas/${personaId}`);
}

export interface CreatePersonaInput {
  name: string;
  description?: string;
  role?: string;
  expertise?: string[];
  tone?: string;
  instructions?: string;
}

export interface UpdatePersonaInput {
  name?: string;
  description?: string;
  role?: string;
  expertise?: string[];
  tone?: string;
  instructions?: string;
}

export async function createPersona(input: CreatePersonaInput): Promise<AlfiePersona> {
  const result = await callAlfieAPI<{ persona: AlfiePersona }>('alfie-personas', 'POST', input);
  return result.persona;
}

export async function updatePersona(personaId: string, input: UpdatePersonaInput): Promise<AlfiePersona> {
  const result = await callAlfieAPI<{ persona: AlfiePersona }>(`alfie-personas/${personaId}`, 'PUT', input);
  return result.persona;
}

export async function getAlfieFiles(): Promise<Array<{ id: string; name: string; size: number; mime_type: string; uploaded_at: string; thread_id?: string }>> {
  const { data, error } = await supabase
    .from('alfie_files')
    .select('id, name, size, mime_type, uploaded_at, thread_id')
    .order('uploaded_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function uploadFiles(
  files: File[],
  projectId?: string,
  personaId?: string,
  isKnowledgeFile?: boolean,
): Promise<FileUpload[]> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session?.access_token) {
    throw new Error('Not authenticated');
  }

  // Validate files BEFORE upload
  const validationErrors: string[] = [];
  files.forEach(file => {
    if (file.size > MAX_FILE_SIZE) {
      validationErrors.push(
        `${file.name} exceeds 25MB limit (${(file.size / 1024 / 1024).toFixed(1)}MB)`
      );
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type) && !file.type.startsWith('text/')) {
      validationErrors.push(`${file.name} type not supported (${file.type})`);
    }
  });

  if (validationErrors.length > 0) {
    throw new Error(`File validation failed:\n${validationErrors.join('\n')}`);
  }

  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  if (projectId) formData.append('projectId', projectId);
  if (personaId) formData.append('personaId', personaId);
  if (isKnowledgeFile) formData.append('isKnowledgeFile', 'true');

  const hermesUrl = import.meta.env.VITE_HERMES_URL || import.meta.env.VITE_SUPABASE_URL;
  const hermesKey = import.meta.env.VITE_HERMES_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

  const response = await fetch(`${hermesUrl}/functions/v1/alfie-files`, {
    method: 'POST',
    headers: {
      ...EDGE_FUNCTION_HEADERS,
      'Authorization': `Bearer ${session.session.access_token}`,
      'apikey': hermesKey,
      // DO NOT set Content-Type - browser sets it with boundary
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Upload failed' }));
    throw new Error(error.message || 'Upload failed');
  }

  const result = await response.json();

  // Handle partial success (some files failed)
  if (result.errors?.length > 0) {
    console.warn('Some files failed to upload:', result.errors);
  }

  return result.files || [];
}

// ============================================================================
// React Query Hooks
// ============================================================================

export function useThreads() {
  // Gated on auth: the widget mounts this globally, and the root route briefly
  // renders the authed layout pre-login — an ungated query fired an anon
  // alfie_threads read on the login screen (2026-07-02 audit, finding 6).
  const { user } = useAuth();
  return useQuery({
    queryKey: ['alfie-threads'],
    // `limit: null` on purpose: the /alfie page's sidebar is an unpaged flat
    // list, and inheriting the default page size would silently truncate a
    // user's older conversations out of it.
    queryFn: () => getThreads({ limit: null }),
    enabled: !!user,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Server-side title search for the widget's history view.
 *
 * Filtering the fetched pages client-side only ever searched what had already
 * been paged in, so a match on chat 60 was invisible until the user pressed
 * "Load more" enough times. Disabled on an empty needle — the paged list owns
 * that case.
 */
export function useThreadSearch(search: string) {
  const { user } = useAuth();
  const needle = search.trim();
  return useQuery({
    queryKey: ['alfie-threads', 'search', needle],
    queryFn: () => getThreads({ search: needle }),
    enabled: !!user && needle.length > 0,
    staleTime: 30_000,
  });
}

/**
 * Paged thread list for the floating widget's history view.
 *
 * Kept separate from `useThreads()` so the /alfie page keeps its flat
 * `Thread[]` shape. The key is prefixed with `['alfie-threads', ...]` so the
 * existing `invalidateQueries({ queryKey: ['alfie-threads'] })` calls refresh
 * both surfaces.
 */
export function useThreadsPaged(pageSize = THREADS_PAGE_SIZE) {
  const { user } = useAuth();
  return useInfiniteQuery({
    queryKey: ['alfie-threads', 'paged', pageSize],
    queryFn: ({ pageParam }) => getThreads({ limit: pageSize, offset: pageParam as number }),
    initialPageParam: 0,
    // A short page is the last page. Offsets accumulate on page length rather
    // than pageIndex * pageSize so a deleted row mid-scroll cannot skip one.
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < pageSize ? undefined : allPages.reduce((n, p) => n + p.length, 0),
    enabled: !!user,
    staleTime: 60_000,
  });
}

function useThreadMutation<TArgs>(fn: (args: TArgs) => Promise<void>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alfie-threads'] });
    },
  });
}

export function useRenameThread() {
  return useThreadMutation(({ threadId, title }: { threadId: string; title: string }) =>
    renameThread(threadId, title),
  );
}

export function useArchiveThread() {
  return useThreadMutation(({ threadId }: { threadId: string }) => archiveThread(threadId));
}

export function useSetThreadPinned() {
  return useThreadMutation(({ threadId, pinned }: { threadId: string; pinned: boolean }) =>
    setThreadPinned(threadId, pinned),
  );
}

export function useCommunityThreads() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['alfie-community-threads'],
    queryFn: getCommunityThreads,
    enabled: !!user,
    staleTime: 60_000,
  });
}

export function useThreadMessages(threadId: string | null) {
  return useQuery({
    queryKey: ['alfie-messages', threadId],
    queryFn: () => (threadId ? getThreadMessages(threadId) : Promise.resolve([])),
    enabled: !!threadId,
    // The persisted IndexedDB copy is for instant paint only — it can lag the
    // DB (e.g. a send that appended two messages elsewhere). Treat it as always
    // stale and refetch on every mount so reopening a thread reconciles to the
    // server, while the on-save cache append keeps the paint correct meanwhile.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useProjects() {
  return useQuery({
    queryKey: ['alfie-projects'],
    queryFn: getProjects,
    staleTime: 5 * 60 * 1000,
  });
}

export function useProject(projectId: string | null) {
  return useQuery({
    queryKey: ['alfie-project', projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alfie-projects'] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, input }: { projectId: string; input: UpdateProjectInput }) =>
      updateProject(projectId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['alfie-projects'] });
      queryClient.invalidateQueries({ queryKey: ['alfie-project', variables.projectId] });
    },
  });
}

export function useRegenerateMemory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => regenerateProjectMemory(projectId),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['alfie-project', projectId] });
    },
  });
}

export function usePersonas() {
  return useQuery({
    queryKey: ['alfie-personas'],
    queryFn: getPersonas,
    staleTime: 10 * 60 * 1000,
  });
}

export function usePersona(personaId: string | null) {
  return useQuery({
    queryKey: ['alfie-persona', personaId],
    queryFn: () => getPersona(personaId!),
    enabled: !!personaId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreatePersona() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPersona,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alfie-personas'] });
    },
  });
}

export function useUpdatePersona() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ personaId, input }: { personaId: string; input: UpdatePersonaInput }) =>
      updatePersona(personaId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['alfie-personas'] });
      queryClient.invalidateQueries({ queryKey: ['alfie-persona', variables.personaId] });
    },
  });
}

export function useAlfieFiles() {
  return useQuery({
    queryKey: ['alfie-files'],
    queryFn: getAlfieFiles,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendChatMessage,
    onSuccess: (data, variables) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['alfie-threads'] });
      if (variables.threadId) {
        queryClient.invalidateQueries({ queryKey: ['alfie-messages', variables.threadId] });
      }
    },
  });
}

export function useUploadFiles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      files,
      projectId,
      personaId,
      isKnowledgeFile,
    }: {
      files: File[];
      projectId?: string;
      personaId?: string;
      isKnowledgeFile?: boolean;
    }) => uploadFiles(files, projectId, personaId, isKnowledgeFile),
    onSuccess: (_data, variables) => {
      if (variables.isKnowledgeFile && variables.personaId) {
        queryClient.invalidateQueries({ queryKey: ['alfie-persona', variables.personaId] });
        queryClient.invalidateQueries({ queryKey: ['alfie-personas'] });
      }
      if (variables.isKnowledgeFile && variables.projectId) {
        queryClient.invalidateQueries({ queryKey: ['alfie-project', variables.projectId] });
        queryClient.invalidateQueries({ queryKey: ['alfie-projects'] });
      }
      queryClient.invalidateQueries({ queryKey: ['alfie-files'] });
    },
  });
}

export async function deleteAlfieFile(fileId: string): Promise<void> {
  await callAlfieAPI(`alfie-files/${fileId}`, 'DELETE');
}

export function useDeleteAlfieFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId }: { fileId: string; personaId?: string; projectId?: string }) =>
      deleteAlfieFile(fileId),
    onSuccess: (_data, variables) => {
      if (variables.personaId) {
        queryClient.invalidateQueries({ queryKey: ['alfie-persona', variables.personaId] });
        queryClient.invalidateQueries({ queryKey: ['alfie-personas'] });
      }
      if (variables.projectId) {
        queryClient.invalidateQueries({ queryKey: ['alfie-project', variables.projectId] });
        queryClient.invalidateQueries({ queryKey: ['alfie-projects'] });
      }
      queryClient.invalidateQueries({ queryKey: ['alfie-files'] });
    },
  });
}

// ============================================================================
// File URL Hooks (signed URLs for private alfie-uploads bucket)
// ============================================================================

const SIGNED_URL_TTL = 55 * 60 * 1000; // 55 min (URLs valid 1 hour)

/**
 * Rewrite internal Docker hostnames in signed URLs to the external Supabase URL.
 * Edge functions inside Docker return `http://kong:8000/...` which is unreachable
 * from the browser. Replace with the actual Hermes/Supabase URL.
 */
function rewriteStorageUrl(url: string): string {
  const hermesUrl = import.meta.env.VITE_HERMES_URL || import.meta.env.VITE_SUPABASE_URL;
  if (!hermesUrl) return url;
  // Replace internal Docker host patterns
  return url
    .replace(/^https?:\/\/kong:\d+/, hermesUrl)
    .replace(/^https?:\/\/supabase_storage_[^/]+:\d+/, hermesUrl);
}

/** Fetch a signed download URL for a single file, with LRU localStorage cache. */
export function useFileUrl(fileId: string | null) {
  return useQuery({
    queryKey: ['alfie-file-url', fileId],
    queryFn: async () => {
      const { getCachedFileUrl, setCachedFileUrl } = await import('@/lib/file-cache');
      const cached = getCachedFileUrl(fileId!);
      if (cached) return cached;

      const res = await callAlfieAPI<{ file: FileUpload }>(`alfie-files/${fileId}`);
      const rawUrl = res.file.downloadUrl || res.file.storageUrl;
      const url = rewriteStorageUrl(rawUrl);
      setCachedFileUrl(fileId!, url, SIGNED_URL_TTL);
      return url;
    },
    enabled: !!fileId,
    staleTime: 50 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

/** Batch-fetch signed URLs for multiple files. Checks cache first, fetches misses in parallel. */
export function useFileUrls(fileIds: string[]) {
  return useQuery({
    queryKey: ['alfie-file-urls', ...fileIds],
    queryFn: async () => {
      const { getCachedFileUrl, setCachedFileUrl } = await import('@/lib/file-cache');
      const result = new Map<string, string>();
      const misses: string[] = [];

      for (const id of fileIds) {
        const cached = getCachedFileUrl(id);
        if (cached) {
          result.set(id, cached);
        } else {
          misses.push(id);
        }
      }

      if (misses.length > 0) {
        const fetched = await Promise.all(
          misses.map(async (id) => {
            const res = await callAlfieAPI<{ file: FileUpload }>(`alfie-files/${id}`);
            const rawUrl = res.file.downloadUrl || res.file.storageUrl;
            const url = rewriteStorageUrl(rawUrl);
            setCachedFileUrl(id, url, SIGNED_URL_TTL);
            return { id, url };
          })
        );
        for (const { id, url } of fetched) {
          result.set(id, url);
        }
      }

      return result;
    },
    enabled: fileIds.length > 0,
    staleTime: 50 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

// ============================================================================
// File List Hooks
// ============================================================================

export async function getFiles(): Promise<AlfieFile[]> {
  const { data, error } = await supabase
    .from('alfie_files')
    .select('id, name, size, mime_type, storage_path, lines, project_id, persona_id, uploaded_at')
    .order('uploaded_at', { ascending: false })
    .limit(100);

  if (error) throw error;
  return data || [];
}

export function useFiles() {
  return useQuery({
    queryKey: ['alfie-files'],
    queryFn: getFiles,
    staleTime: 2 * 60 * 1000,
  });
}
